import {createContext, use, useMemo, type ReactNode} from 'react'

interface SidebarAgentFilterContextValue {
  automationId: string | null
  isAgentFilterActive: boolean
  automationsVisible: boolean
}

const defaultValue: SidebarAgentFilterContextValue = {
  automationId: null,
  isAgentFilterActive: false,
  automationsVisible: false,
}
const SidebarAgentFilterContext = createContext<SidebarAgentFilterContextValue>(defaultValue)

/**
 * Provider for sidebar agent filtering.
 * - automationId=null (or omitted): no filter active
 * - automationId="some-id": filter by this automation
 * - automationsVisible: server-resolved flag controlling whether the Automations
 *   area (and the agent filter UI) is visible to the current user on this repo.
 *   When false/undefined, `useSidebarAgentFilter` returns the inert default value.
 */
export function SidebarAgentFilterProvider({
  automationId = null,
  automationsVisible = false,
  children,
}: {
  automationId?: string | null
  automationsVisible?: boolean
  children: ReactNode
}) {
  const value = useMemo(
    () => ({
      automationId,
      isAgentFilterActive: automationId !== null,
      automationsVisible,
    }),
    [automationId, automationsVisible],
  )
  return <SidebarAgentFilterContext value={value}>{children}</SidebarAgentFilterContext>
}

export function useSidebarAgentFilter(): SidebarAgentFilterContextValue {
  const context = use(SidebarAgentFilterContext)
  if (!context.automationsVisible) {
    return defaultValue
  }
  return context
}
